import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  DateHelper,
  LoaderService,
  MessageService,
  ValidationResult,
  ValidationResultService,
} from '@shared';
import {
  AlertsService,
  GlobalEventsService,
  ChangesetService,
} from '@shared-data';
import { TokenService } from '@auth';
import { environment } from 'src/environments/environment';
import { PendingChangesGridComponent } from '../pending-changes-grid/pending-changes-grid.component';
import { CheckBoxState } from '@progress/kendo-angular-inputs';
import {
  catchError,
  combineLatest,
  finalize,
  forkJoin,
  map,
  mapTo,
  of,
  tap,
} from 'rxjs';

type SyncOptions = 'now' | 'scheduled' | 'lab';
@Component({
  selector: 'app-scheduler',
  templateUrl: './scheduler.component.html',
  styleUrl: './scheduler.component.scss',
})
export class SchedulerComponent implements OnInit, OnDestroy {
  scheduledDate: Date | null = null;
  syncOption: SyncOptions = 'scheduled';

  now = new Date();

  showSyncWarning = false;

  @Output()
  onConfirm: EventEmitter<void> = new EventEmitter<void>();

  @ViewChildren(PendingChangesGridComponent)
  changesGrids!: QueryList<PendingChangesGridComponent>;

  @Input()
  width = 1250;

  height = window.innerHeight * 0.8;

  ngOnDestroy() {
    window.removeEventListener('resize', this.updateHeight.bind(this));
  }

  private updateHeight() {
    this.height = window.innerHeight * 0.8;
  }

  disableSyncNow = environment.disableSyncNow;

  downloadBackup: CheckBoxState = true;

  disableOptionSync = false;

  pricingCount = 0;
  availabilityCount = 0;
  overridesCount = 0;
  modifierCodesCount = 0;

  isAvailabilityOnly: boolean | null = null;

  modifierCodesFeatureEnabled: boolean = environment.modifierCodesFeatureEnabled;

  constructor(
    private globalEventsService: GlobalEventsService,
    private changesetService: ChangesetService,
    private loaderService: LoaderService,
    private messageService: MessageService,
    private validationErrorService: ValidationResultService,
    private tokenService: TokenService,
  ) {}

  async ngOnInit() {
    // Load role before initializing the component
    this.isAvailabilityOnly = await this.tokenService.isAvailabilityOnly();
    
    this.onSyncOptionChange(this.syncOption);
    window.addEventListener('resize', this.updateHeight.bind(this));
    this.updateHeight();
  }

  ngAfterViewInit() {
    // Subscribe to changes in the QueryList (grids may not be available initially due to *ngIf)
    this.changesGrids.changes.subscribe(() => {
      this.subscribeToGridChanges();
    });
    
    // Also subscribe immediately if grids are already available
    this.subscribeToGridChanges();
  }

  private subscribeToGridChanges() {
    if (this.changesGrids.length === 0) {
      return;
    }

    const gridObservables = this.changesGrids.map((grid) =>
      grid.dataChanged.pipe(
        map((changes) => ({
          type: grid.type,
          selectedCount: changes.total
        })),
      ),
    );

    
    combineLatest(gridObservables).subscribe((gridStates) => {
      const countByType = Object.fromEntries(
        gridStates.map((state) => [state.type, state.selectedCount]),
      );

      this.pricingCount = countByType['pricing'] ?? 0;
      this.availabilityCount = countByType['availability'] ?? 0;
      this.overridesCount = countByType['overrides'] ?? 0;
      this.modifierCodesCount = countByType['modifiercodes'] ?? 0;
      const totalSelected = Object.values(countByType).reduce(
        (sum, count) => sum + count,
        0,
      );

      if (this.syncOption === 'lab') {
        this.disableOptionSync = totalSelected === 0;
        if (this.disableOptionSync) {
          this.syncOption = 'scheduled';
          this.onSyncOptionChange('scheduled');
        }
      }
    });
  }

  onCancel() {
    this.onConfirm.emit();
  }

  onAccept() {
    if (this.downloadBackup) {
      this.downloadBackupThenScheduleChanges();
    } else {
      this.scheduleChanges();
    }
  }

  private triggerDownload(
    data: BlobPart,
    filename: string,
    mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ) {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  downloadBackupThenScheduleChanges() {
    const isLab = this.syncOption === 'lab';

    const observables = [];

    // Only download pricing backup if user is NOT AvailabilityOnly
    if (this.pricingCount > 0 && !this.isAvailabilityOnly) {
      const pricing$ = this.changesetService
        .downloadBackup(isLab, 'pricing')
        .pipe(
          tap((data) =>
            this.triggerDownload(
              data,
              `Pricing Reverse Backup ${new Date().toLocaleString('en-US')}.xlsx`,
            ),
          ),
          catchError((err) => of({ key: 'pricing', ok: false as const, err })),
        );
      observables.push(pricing$);
    }

    if (this.availabilityCount > 0) {
      const availability$ = this.changesetService
        .downloadBackup(isLab, 'availability')
        .pipe(
          tap((data) =>
            this.triggerDownload(
              data,
              `Availability Reverse Backup ${new Date().toLocaleString('en-US')}.xlsx`,
            ),
          ),
          catchError((err) =>
            of({ key: 'availability', ok: false as const, err }),
          ),
        );
      observables.push(availability$);
    }

    if (observables.length === 0) {
      this.scheduleChanges();
      return;
    }

    this.loaderService.show();

    forkJoin(observables)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: () => {
          this.scheduleChanges();
        },
        error: (errors) => {
          const validationResult = new ValidationResult();
          for (const error of errors) {
            if (error.error) {
              validationResult.errors.push(error.err.error);
            } else {
              validationResult.errors.push(
                error?.message ?? 'Unexpected error',
              );
            }
          }
          this.validationErrorService.show(validationResult);
          this.messageService.error(`Backup download failed`);
        },
      });
  }

  scheduleChanges() {
    this.loaderService.show();
    const changes = this.changesGrids.toArray().flatMap((grid) =>
      grid.changeset.data
        .filter((change) => change.selected)
        .map((change) => ({
          ...change,
          scheduledDate: this.scheduledDate,
        })),
    );

    this.changesetService
      .schedulePendingChanges(changes, this.syncOption == 'lab')
      .subscribe({
        next: () => {
          this.globalEventsService.updatePendingChanges();
          this.globalEventsService.updateTiers();
          this.loaderService.hide();
          this.onConfirm.emit();
        },
        error: (err) => {
          const validationError = err?.error;
          this.messageService.error(validationError);
          this.loaderService.hide();
        },
      });
  }

  onSyncOptionChange(syncOption: SyncOptions) {
    if (syncOption === 'scheduled') {
      this.scheduledDate = DateHelper.endOfBusinessDay(new Date());
    } else {
      this.scheduledDate = null;
    }
  }
}
